// Market Forge — Strategy Tracker widget.
// Visualises how the GEX classification for the current symbol has evolved
// over the last N days. Pulls /api/forge/gex/classifier and renders:
//   - A horizontal stacked distribution bar (PREMIUM_ROTATION / NORMAL /
//     NEUTRAL / ASSIGNMENT_ACCEPTED / DANGER_PREMIUM) with percentage.
//   - Current classification + dominant classification + stability %.
//   - Last 5 transitions ("NORMAL_CSP → PREMIUM_ROTATION_CSP · 2d ago").
//
// Per PRD: this is a P2 feature that complements the static GEX panel with a
// historical regime-evolution view. Never overrides ROI/WES — informational.

import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, GEX_CLASS_COLORS } from "./utils";

function timeAgo(iso, t) {
  if (!iso) return "—";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 60) return t("strategy_tracker.ago.mins", "{n}m ago").replace("{n}", mins);
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return t("strategy_tracker.ago.hrs", "{n}h ago").replace("{n}", hrs);
    const days = Math.round(hrs / 24);
    return t("strategy_tracker.ago.days", "{n}d ago").replace("{n}", days);
  } catch { return "—"; }
}

export function StrategyTracker({ symbol, signModel, t }) {
  const [days, setDays] = useState(() => parseInt(localStorage.getItem("forge.gex.strategy_days") || "30", 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { localStorage.setItem("forge.gex.strategy_days", String(days)); }, [days]);

  useEffect(() => {
    if (!symbol) return;
    let alive = true;
    const fetchIt = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/forge/gex/classifier`, {
          params: { symbol, days, sign_model: signModel || "dealer_short" },
        });
        if (alive) setData(r.data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchIt();
    const id = setInterval(fetchIt, 120000);  // refresh every 2m while open
    return () => { alive = false; clearInterval(id); };
  }, [symbol, days, signModel]);

  if (!data) {
    return (
      <div data-testid="gex-strategy-tracker-empty" className="text-[10px] font-mono text-[#5F5F66] text-center py-2 border border-dashed border-[#26262B] rounded-sm">
        {loading ? t("strategy_tracker.loading") : t("strategy_tracker.empty")}
      </div>
    );
  }

  if (data.total_snapshots === 0) {
    return (
      <div data-testid="gex-strategy-tracker-empty" className="text-[10px] font-mono text-[#5F5F66] text-center py-2 border border-dashed border-[#26262B] rounded-sm">
        {t("strategy_tracker.collecting")}
      </div>
    );
  }

  return (
    <div data-testid="gex-strategy-tracker" className="bg-[#0A0A0D] border border-[#26262B] rounded-sm p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#8A8A93]">
          {t("strategy_tracker.title")} · {data.total_snapshots} {t("strategy_tracker.snaps")}
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              data-testid={`strategy-days-${d}`}
              onClick={() => setDays(d)}
              className={`text-[9px] font-mono px-1.5 py-0.5 rounded-sm border ${days === d ? "border-[#00FF88] text-[#00FF88] bg-[#00FF88]/10" : "border-[#26262B] text-[#8A8A93] hover:text-white"}`}
            >{d}d</button>
          ))}
        </div>
      </div>

      {/* Stacked distribution bar */}
      <div data-testid="strategy-distribution" className="flex h-3 rounded-sm overflow-hidden border border-[#26262B]">
        {data.classifications.map((c) => (
          <div
            key={c.classification}
            title={`${c.classification}: ${c.pct}% (${c.count})`}
            style={{
              width: `${c.pct}%`,
              background: GEX_CLASS_COLORS[c.classification] || "#5F5F66",
              opacity: 0.85,
            }}
            data-testid={`strategy-bucket-${c.classification}`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-1">
        {data.classifications.map(c => (
          <div key={c.classification} className="flex items-center justify-between text-[10px] font-mono">
            <div className="flex items-center gap-1.5 truncate">
              <span className="inline-block w-2 h-2 rounded-sm flex-shrink-0"
                style={{ background: GEX_CLASS_COLORS[c.classification] || "#5F5F66" }} />
              <span className="text-[#E4E4E7] truncate">{t(`gex.class.${c.classification}`, c.classification)}</span>
            </div>
            <span className="text-[#8A8A93] ml-1">{c.pct}%</span>
          </div>
        ))}
      </div>

      {/* Current / dominant / stability */}
      <div className="grid grid-cols-3 gap-2 pt-1.5 border-t border-[#26262B]">
        <div>
          <div className="text-[8px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("strategy_tracker.current")}</div>
          <div data-testid="strategy-current" className="text-[10px] font-bold font-mono"
            style={{ color: GEX_CLASS_COLORS[data.current_classification] || "#E4E4E7" }}>
            {t(`gex.class.${data.current_classification}`, data.current_classification || "—")}
          </div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("strategy_tracker.dominant")}</div>
          <div data-testid="strategy-dominant" className="text-[10px] font-bold font-mono"
            style={{ color: GEX_CLASS_COLORS[data.dominant_classification] || "#E4E4E7" }}>
            {t(`gex.class.${data.dominant_classification}`, data.dominant_classification || "—")}
          </div>
        </div>
        <div>
          <div className="text-[8px] uppercase tracking-[0.15em] text-[#8A8A93]">{t("strategy_tracker.stability")}</div>
          <div data-testid="strategy-stability" className="text-[10px] font-bold font-mono text-white">
            {data.stability_pct != null ? `${data.stability_pct}%` : "—"}
          </div>
        </div>
      </div>

      {/* Transitions */}
      {data.transitions.length > 0 && (
        <div className="pt-1.5 border-t border-[#26262B] space-y-1">
          <div className="text-[8px] uppercase tracking-[0.15em] text-[#8A8A93]">
            {t("strategy_tracker.transitions")} · {data.transition_count}
          </div>
          <div className="space-y-0.5 max-h-24 overflow-y-auto">
            {data.transitions.slice(-5).reverse().map((tr, i) => (
              <div key={i} data-testid={`strategy-transition-${i}`}
                className="text-[10px] font-mono flex items-center justify-between gap-2">
                <div className="flex items-center gap-1 truncate">
                  <span style={{ color: GEX_CLASS_COLORS[tr.from] || "#8A8A93" }}>{t(`gex.class.${tr.from}`, tr.from)}</span>
                  <span className="text-[#5F5F66]">→</span>
                  <span style={{ color: GEX_CLASS_COLORS[tr.to] || "#8A8A93" }}>{t(`gex.class.${tr.to}`, tr.to)}</span>
                </div>
                <span className="text-[#5F5F66] text-[9px] flex-shrink-0">{timeAgo(tr.at, t)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
